import {
  IPayrollChangeRequest,
  IPayrollChangeRequestData,
  IPayrollChangeRequestFieldError,
  IPayrollChangeRequestFieldErrorData,
  IPayrollChangeRequestStatusData,
  PayrollChangeRequestStatusEnum,
  IPayrollChangeRequestStatus
} from './payrollchangerequest.types';
import { Department, User } from '@/models';
import { IUserData, IUserFieldError, UserFieldError } from '../user';
import { IDepartmentData } from '../department';
import {
  Employee,
  EmployeeFieldError,
  IEmployeeData,
  IEmployeeFieldError
} from '../employee';
import { IWorkgroupData } from '../workgroup';
import moment from 'moment';
import { State } from '../state/state';

export class PayrollChangeRequest implements IPayrollChangeRequest {
  readonly id?: number;
  readonly state?: IPayrollChangeRequestStatus = new PayrollChangeRequestStatus();
  readonly employee: number | IEmployeeData | null = null;
  readonly from_department: IDepartmentData | null = null;
  readonly to_department?: IDepartmentData | null = null;
  readonly from_group?: IWorkgroupData | null = null;
  readonly to_group?: IWorkgroupData | null = null;
  readonly from_rate?: number | null = null;
  readonly to_rate?: number | null = null;
  readonly reason: string = '';
  readonly additional_info?: string = '';
  readonly owner?: IUserData | number;
  readonly effective_date?: string | Date = moment(new Date()).format(
    'YYYY-MM-DD'
  );
  readonly created_at?: string | Date;
  readonly updated_at?: string | Date;
  readonly hr_personnel?: number | IUserData | null = null;

  constructor(data?: IPayrollChangeRequestData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.state) {
        this.state = new PayrollChangeRequestStatus(data.state);
      }

      if (data.employee) {
        this.employee =
          typeof data.employee === 'number'
            ? data.employee
            : new Employee(data.employee);
      }

      if (data.from_department) {
        this.from_department =
          typeof data.from_department === 'number'
            ? data.from_department
            : new Department(data.from_department);
      }

      if (data.to_department) {
        this.to_department = data.to_department;
      }

      if (data.from_group) {
        this.from_group = data.from_group;
      }

      if (data.to_group) {
        this.to_group = data.to_group;
      }

      if (data.from_rate) {
        this.from_rate = data.from_rate;
      }

      if (data.to_rate) {
        this.to_rate = data.to_rate;
      }

      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.additional_info) {
        this.additional_info = data.additional_info;
      }

      if (data.owner) {
        this.owner = new User(data.owner as IUserData);
      }

      if (data.effective_date) {
        this.effective_date = data.effective_date;
      }

      if (data.created_at) {
        this.created_at = data.created_at;
      }

      if (data.updated_at) {
        this.updated_at = data.updated_at;
      }

      if (data.effective_date) {
        this.effective_date = data.effective_date;
      }

      if (data.hr_personnel) {
        this.hr_personnel =
          typeof data.hr_personnel === 'number'
            ? data.hr_personnel
            : new User(data.hr_personnel);
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

class PayrollChangeRequestState extends State<PayrollChangeRequestStatusEnum> {
  static readonly Draft = new State({
    value: PayrollChangeRequestStatusEnum.Draft,
    display_name:
      PayrollChangeRequestStatusEnum[PayrollChangeRequestStatusEnum.Draft],
    message: 'Draft',
    icon: 'mdi-file-document-outline',
    color: 'draft'
  });

  static readonly Submitted = new State({
    value: PayrollChangeRequestStatusEnum.Submitted,
    display_name:
      PayrollChangeRequestStatusEnum[PayrollChangeRequestStatusEnum.Submitted],
    message: 'Submitted for approval',
    icon: 'mdi-file-send-outline',
    color: 'submitted'
  });

  public static readonly InReview = new State({
    value: PayrollChangeRequestStatusEnum.InReview,
    display_name:
      PayrollChangeRequestStatusEnum[PayrollChangeRequestStatusEnum.InReview],
    message: 'Review In Progress',
    icon: 'mdi-file-send-outline',
    color: 'submitted'
  });

  public static readonly Cancelled = new State({
    value: PayrollChangeRequestStatusEnum.Cancelled,
    display_name:
      PayrollChangeRequestStatusEnum[PayrollChangeRequestStatusEnum.Cancelled],
    message: 'Cancelled',
    icon: 'mdi-file-send-outline',
    color: 'cancel'
  });

  public static readonly Complete = new State({
    value: PayrollChangeRequestStatusEnum.Complete,
    display_name:
      PayrollChangeRequestStatusEnum[PayrollChangeRequestStatusEnum.Complete],
    message: 'Complete',
    icon: 'mdi-file-send-outline',
    color: 'done'
  });

  public static readonly Default = new State({
    value: -1,
    display_name: '',
    color: 'white'
  });
}

export class PayrollChangeRequestStatus
  implements IPayrollChangeRequestStatusData {
  readonly value: number = PayrollChangeRequestStatusEnum.Draft;
  readonly display_name?: string = 'Draft';

  constructor(data?: IPayrollChangeRequestStatusData) {
    if (data) {
      this.value = data.value;
      if (data.display_name) {
        this.display_name = data.display_name;
      }
    }
  }

  getCurrentState(): State<PayrollChangeRequestStatusEnum> {
    let state = null;
    switch (this.value) {
      case PayrollChangeRequestStatusEnum.Draft:
        state = PayrollChangeRequestState.Draft;
        break;

      case PayrollChangeRequestStatusEnum.Submitted:
        state = PayrollChangeRequestState.Submitted;
        break;

      case PayrollChangeRequestStatusEnum.InReview:
        state = PayrollChangeRequestState.InReview;
        break;

      case PayrollChangeRequestStatusEnum.Cancelled:
        state = PayrollChangeRequestState.Cancelled;
        break;

      case PayrollChangeRequestStatusEnum.Complete:
        state = PayrollChangeRequestState.Complete;
        break;

      default:
        state = PayrollChangeRequestState.Default;
    }
    return state;
  }
}
export class PayrollChangeRequestFieldError
  implements IPayrollChangeRequestFieldError {
  readonly employee: IEmployeeFieldError = new EmployeeFieldError();
  readonly from_department: string[] = [];
  readonly to_department: string[] = [];
  readonly from_group: string[] = [];
  readonly to_group: string[] = [];
  readonly from_rate: string[] = [];
  readonly to_rate: string[] = [];
  readonly reason: string[] = [];
  readonly additional_info: string[] = [];
  readonly owner: string[] = [];
  readonly effective_date: string[] = [];
  readonly hr_personnel: IUserFieldError = new UserFieldError();

  constructor(data?: IPayrollChangeRequestFieldErrorData) {
    if (data) {
      if (data.employee) {
        this.employee = new EmployeeFieldError(data.employee);
      }

      if (data.from_department) {
        this.from_department = data.from_department;
      }

      if (data.to_department) {
        this.to_department = data.to_department;
      }

      if (data.from_group) {
        this.from_group = data.from_group;
      }

      if (data.to_group) {
        this.to_group = data.to_group;
      }

      if (data.from_rate) {
        this.from_rate = data.from_rate;
      }

      if (data.to_rate) {
        this.to_rate = data.to_rate;
      }

      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.additional_info) {
        this.additional_info = data.additional_info;
      }

      if (data.owner) {
        this.owner = data.owner;
      }

      if (data.effective_date) {
        this.effective_date = data.effective_date;
      }

      if (data.hr_personnel) {
        this.hr_personnel = new UserFieldError(data.hr_personnel);
      }
    }
  }

  clearErrors(): void {
    this.employee.clearErrors();

    this.from_department.splice(0, this.from_department.length);

    this.to_department.splice(0, this.to_department.length);

    this.from_group.splice(0, this.from_group.length);

    this.to_group.splice(0, this.to_group.length);

    this.from_rate.splice(0, this.from_rate.length);
    this.to_rate.splice(0, this.to_rate.length);

    this.reason.splice(0, this.reason.length);

    this.additional_info.splice(0, this.additional_info.length);
    this.owner.splice(0, this.owner.length);
    this.effective_date.splice(0, this.effective_date.length);
    this.hr_personnel.clearErrors();
  }

  static isPayrollChangeRequestFieldError(
    data: IPayrollChangeRequestFieldErrorData
  ): boolean {
    if (data) {
      if (
        data.employee &&
        EmployeeFieldError.isEmployeeFieldError(data.employee)
      ) {
        return true;
      } else if (data.from_department && Array.isArray(data.from_department)) {
        return true;
      } else if (data.to_department && Array.isArray(data.to_department)) {
        return true;
      } else if (data.from_group && Array.isArray(data.from_group)) {
        return true;
      } else if (data.to_group && Array.isArray(data.to_group)) {
        return true;
      } else if (data.from_rate && Array.isArray(data.from_rate)) {
        return true;
      } else if (data.to_rate && Array.isArray(data.to_rate)) {
        return true;
      } else if (data.reason && Array.isArray(data.reason)) {
        return true;
      } else if (data.additional_info && Array.isArray(data.additional_info)) {
        return true;
      } else if (data.owner && Array.isArray(data.owner)) {
        return true;
      } else if (data.effective_date && Array.isArray(data.effective_date)) {
        return true;
      } else if (
        data.hr_personnel &&
        UserFieldError.isUserFieldError(data.hr_personnel)
      ) {
        return true;
      }
    }
    return false;
  }
}
